import { useMemo } from "react";
import { sumBy } from "lodash";
import { Flex } from "styles/utility-components";

import {
  isQueryableSpendFilter,
  SPEND_BUYER_TYPE_TIME_UNIT,
  SpendFilters,
} from "lib/hooks/api/spend/spendFilters";
import { useSpendOverTimeByBuyerType } from "lib/hooks/api/spend/useSpendOverTimeByBuyerType";
import { TrackingProvider } from "lib/tracking";
import { SpendExportGroupBy } from "lib/types/graphQLEnums";
import { withBuyerTypeColours } from "../buyerTypeColours";
import ExportDataButton from "../ExportDataButton";
import { SpendRankingSort } from "../hooks/useRankingTable";
import { SpendView } from "../hooks/useSpendUrlState";
import { EmptyState, SpendChartError } from "../pageStates";
import SpendChart from "../SpendChart";
import {
  buildGroupedSpendDatasets,
  buyerTypePointsAsGrouped,
  rankGroupedSpend,
} from "../spendChartUtils";
import { BuyerTypeRankingTable } from "./BuyerTypeRankingTable";

type Props = {
  filters: SpendFilters;
  view: SpendView;
  onViewChange: (update: Partial<SpendView>) => void;
};

/**
 * Trends grouped by buyer type: how the spend splits across kinds of public body. One
 * query feeds both the chart and the ranking, and it uses the date range as set (not
 * snapped to whole periods), so its totals match the trend over time.
 */
export function BuyerTypeRankingView({ filters, view, onViewChange }: Props) {
  const { measure, sortOrder, interval, chartType } = view;
  const sort = useMemo<SpendRankingSort>(() => ({ measure, sortOrder }), [measure, sortOrder]);

  const { data, isLoading, isError, error, refetch } = useSpendOverTimeByBuyerType(
    { filters, dateUnit: SPEND_BUYER_TYPE_TIME_UNIT[interval] },
    { enabled: isQueryableSpendFilter(filters) },
  );

  const points = useMemo(() => buyerTypePointsAsGrouped(data?.points ?? []), [data?.points]);
  const chartData = useMemo(
    () => withBuyerTypeColours(buildGroupedSpendDatasets(points, measure)),
    [points, measure],
  );
  const rows = useMemo(() => rankGroupedSpend(points, sort), [points, sort]);
  const transactionCount = sumBy(rows, (r) => r.transactionCount);

  if (isError) {
    return <SpendChartError error={error} onRetry={refetch} />;
  }
  if (!isLoading && transactionCount === 0) {
    return <EmptyState filters={filters} />;
  }

  return (
    <Flex column gap={24}>
      <TrackingProvider data={{ "Context source": "Buyer type spending chart" }}>
        <SpendChart
          data={chartData}
          dateUnit={interval}
          isLoading={isLoading}
          measure={measure}
          chartType={chartType}
        />
      </TrackingProvider>
      <BuyerTypeRankingTable
        rows={rows}
        sort={sort}
        onSortChange={onViewChange}
        isLoading={isLoading}
        action={
          <ExportDataButton
            filters={filters}
            totalRows={transactionCount}
            isLoadingTotalRows={isLoading}
            groupBy={SpendExportGroupBy.None}
          />
        }
      />
    </Flex>
  );
}
